import { ChangeEvent, KeyboardEvent, MouseEvent, useCallback, useState } from 'react';
import { CancelIcon, PlusGrayIcon } from '../icons';
import { Icon } from './Icon';
import { TextInput } from './TextInput';

export interface RangeNumberInputProps {
  clearable?: boolean;
  label?: string;
  value: [string | undefined, string | undefined];
  onChange: (value: Array<string | undefined>) => void;
}

export const RangeNumberInput = ({
  label,
  clearable = false,
  value: initValue,
  onChange,
}: RangeNumberInputProps) => {
  const [redactMode, setRedactMode] = useState(false);
  const [value, setValue] = useState<Array<string | undefined>>(initValue);

  const handleClear = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      setValue([]);
      onChange([]);
    },
    [onChange, setValue],
  );

  const handleChangeMin = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setValue(value => [e.target.value, value?.[1]]);
  }, []);

  const handleChangeMax = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setValue(value => [value?.[0], e.target.value]);
  }, []);

  const handleSave = useCallback(() => {
    setRedactMode(false);
    onChange(value);
  }, [onChange, value]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.code === 'Enter') {
        handleSave();
      }
    },
    [handleSave],
  );
  return (
    <div className="flex flex-col border-b text-left">
      {label && (
        <label className="text-[10px] leading-[18px] text-gray-900 text-opacity-[.64] uppercase font-medium">
          {label}
        </label>
      )}
      {redactMode ? (
        <div
          className="flex flex-row justify-between w-full items-center"
          onKeyDown={handleKeyDown}
        >
          <TextInput
            autoFocus
            placeholder="min"
            name="min"
            type="number"
            className="!min-w-[100px] !w-[100px] border-none"
            value={value?.[0]}
            onChange={handleChangeMin}
          />
          <span>-</span>
          <TextInput
            placeholder="max"
            name="max"
            type="number"
            className="!min-w-[100px] !w-[100px] border-none"
            value={value?.[1]}
            onChange={handleChangeMax}
          />
          <button onClick={handleSave}>
            <img alt="apply" src={PlusGrayIcon} />
          </button>
        </div>
      ) : (
        <div
          className="flex flex-row justify-between group cursor-pointer"
          onClick={setRedactMode.bind(null, true)}
        >
          <span className="py-2 text-gray-900 text-opacity-[.64]">
            {initValue?.[0] ?? '---'} - {initValue?.[1] ?? '---'}
          </span>
          {clearable && value.some(Boolean) && (
            <button onClick={handleClear} className="hidden group-hover:block">
              <Icon Source={CancelIcon} color="gray" className="w-4 h-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};
